import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (templateId: string) => void;
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  backgroundColor: z.string().default('#FFFFFF'),
  textColor: z.string().default('#000000'),
  borderColor: z.string().default('#000000'),
  fontFamily: z.string().default('helvetica'),
  customCss: z.string().optional(),
});

export function CreateTemplateDialog({ open, onOpenChange, onSuccess }: CreateTemplateDialogProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      backgroundColor: "#FFFFFF",
      textColor: "#000000",
      borderColor: "#000000",
      fontFamily: "helvetica",
      customCss: "",
    },
  });
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { data, error } = await supabase
        .from('certificate_templates')
        .insert([
          {
            name: values.name,
            description: values.description,
            background_color: values.backgroundColor,
            text_color: values.textColor,
            border_color: values.borderColor,
            font_family: values.fontFamily,
            custom_css: values.customCss,
            is_active: true,
          }
        ])
        .select();
      
      if (error) throw error;
      
      toast.success("Template created", "Your new certificate template has been created successfully");
      
      onOpenChange(false);
      form.reset();
      
      // Call the onSuccess callback with the new template ID
      if (data && data.length > 0 && onSuccess) {
        onSuccess(data[0].id);
      }
      
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error("Error", "There was a problem creating the template");
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Certificate Template</DialogTitle>
          <DialogDescription>
            Create a new certificate template with custom styling
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Course Completion Certificate" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Template for course completion certificates" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="backgroundColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Background Color</FormLabel>
                    <div className="flex gap-2 items-center">
                      <FormControl>
                        <Input type="color" {...field} className="w-12 h-8" />
                      </FormControl>
                      <Input type="text" {...field} className="flex-1" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="textColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text Color</FormLabel>
                    <div className="flex gap-2 items-center">
                      <FormControl>
                        <Input type="color" {...field} className="w-12 h-8" />
                      </FormControl>
                      <Input type="text" {...field} className="flex-1" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="borderColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Border Color</FormLabel>
                    <div className="flex gap-2 items-center">
                      <FormControl>
                        <Input type="color" {...field} className="w-12 h-8" />
                      </FormControl>
                      <Input type="text" {...field} className="flex-1" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="fontFamily"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Font Family</FormLabel>
                    <FormControl>
                      <Input placeholder="helvetica" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="customCss"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom CSS (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder=".certificate { border: 2px solid #000; }" className="font-mono" {...field} />
                  </FormControl>
                  <FormDescription>
                    Add custom CSS styling for your certificate template
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">Create Template</Button>
            </DialogFooter>
          </form>
        </Form>
        
      </DialogContent>
    </Dialog>
  );
}
